import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useDocPrefsStore } from '../../stores/docPrefsStore';
import { useAuth } from './AuthContext';
import { useThemeMode } from '../../hooks/useThemeMode';
import { useOrgTier } from '../../hooks/useOrgTier';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ThemeContext = createContext();

// Hard-coded fallbacks used only when:
//  - the SuperAdmin site_config hasn't been loaded yet, OR
//  - both fields are empty (legacy DB).
// In every other case we honour whatever the SuperAdmin picked under
// Branding → Theme Defaults.
export const FALLBACK_DEFAULT_LIGHT = '#1a1a2e'; // Midnight
export const FALLBACK_DEFAULT_DARK = '#f59e0b';  // Gold / amber-500

// Kept named-exported for legacy callers that import them directly. They now
// represent the *current* SuperAdmin defaults — refreshed when site_config
// loads. Components that need them at import time should still expect the
// fallback first paint; the next paint will reflect the SuperAdmin values.
export let DEFAULT_COLOR_LIGHT = FALLBACK_DEFAULT_LIGHT;
export let DEFAULT_COLOR_DARK = FALLBACK_DEFAULT_DARK;

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : { r: 26, g: 26, b: 46 };
}

function lighten(hex, amount) {
  const { r, g, b } = hexToRgb(hex);
  const lr = Math.min(255, r + Math.round((255 - r) * amount));
  const lg = Math.min(255, g + Math.round((255 - g) * amount));
  const lb = Math.min(255, b + Math.round((255 - b) * amount));
  return `rgb(${lr}, ${lg}, ${lb})`;
}

function darken(hex, amount) {
  const { r, g, b } = hexToRgb(hex);
  const dr = Math.max(0, Math.round(r * (1 - amount)));
  const dg = Math.max(0, Math.round(g * (1 - amount)));
  const db = Math.max(0, Math.round(b * (1 - amount)));
  return `rgb(${dr}, ${dg}, ${db})`;
}

function applyThemeToDOM(color, isDark) {
  const root = document.documentElement;
  root.style.setProperty('--theme-primary', color);
  root.style.setProperty('--theme-primary-hover', darken(color, isDark ? 0.10 : 0.15));
  root.style.setProperty(
    '--theme-primary-light',
    isDark ? `rgba(${hexToRgb(color).r}, ${hexToRgb(color).g}, ${hexToRgb(color).b}, 0.18)` : lighten(color, 0.92),
  );
  root.style.setProperty('--theme-primary-medium', lighten(color, isDark ? 0.40 : 0.82));
  root.style.setProperty('--theme-primary-text', color);
  root.style.setProperty('--theme-primary-border', lighten(color, isDark ? 0.55 : 0.7));
}

const cachedThemeColor = (() => {
  try { return localStorage.getItem('app.theme_color') || null; } catch { return null; }
})();
const cachedSystemDefaults = (() => {
  try { return JSON.parse(localStorage.getItem('app.system_defaults') || 'null'); } catch { return null; }
})();

const HEX = /^#[0-9a-fA-F]{6}$/;
const sanitise = (c, fallback) => (HEX.test(String(c || '').trim()) ? String(c).trim().toLowerCase() : fallback);

export function ThemeProvider({ children }) {
  const { isDark } = useThemeMode();
  const { isPro } = useOrgTier();
  const { user } = useAuth();

  const [userPickedColor, setUserPickedColor] = useState(cachedThemeColor || null);
  const [systemDefaults, setSystemDefaults] = useState(() => ({
    light: sanitise(cachedSystemDefaults?.light, FALLBACK_DEFAULT_LIGHT),
    dark: sanitise(cachedSystemDefaults?.dark, FALLBACK_DEFAULT_DARK),
  }));
  const [loaded, setLoaded] = useState(false);

  // Keep the named exports in sync so legacy callers
  // (AppearanceTab default-resolution, hint chip text) read the latest values.
  useEffect(() => {
    DEFAULT_COLOR_LIGHT = systemDefaults.light;
    DEFAULT_COLOR_DARK = systemDefaults.dark;
  }, [systemDefaults]);

  // Sentinel set is dynamic — derived from the SuperAdmin-controlled defaults.
  const sentinels = new Set([
    systemDefaults.light.toLowerCase(),
    systemDefaults.dark.toLowerCase(),
  ]);
  const isSentinel = (c) => !!c && sentinels.has(String(c).trim().toLowerCase());

  const modeDefault = isDark ? systemDefaults.dark : systemDefaults.light;

  // Standard-tier orgs are locked to the system defaults (no custom colors).
  // Pro orgs: pass through any saved hex; sentinels still auto-swap.
  const effective = !isPro
    ? modeDefault
    : !userPickedColor || isSentinel(userPickedColor)
      ? modeDefault
      : userPickedColor;

  useEffect(() => {
    applyThemeToDOM(effective, isDark);
    try { localStorage.setItem('app.theme_color_effective', effective); } catch {}
    try { window.dispatchEvent(new Event('theme-color-changed')); } catch {}
  }, [effective, isDark]);

  const loadTheme = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const data = await useDocPrefsStore.getState().fetch();
        const picked = (data?.theme_color || '').trim() || null;
        setUserPickedColor(picked);
        if (picked) {
          try { localStorage.setItem('app.theme_color', picked); } catch {}
        } else {
          try { localStorage.removeItem('app.theme_color'); } catch {}
        }
      }
    } catch {
      /* keep cache */
    }
    setLoaded(true);
  }, []);

  // Pull SuperAdmin-controlled defaults from the public site-config endpoint
  // (no auth required, so this works on the AuthPage too).
  const loadSystemDefaults = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/site-config/public`);
      const next = {
        light: sanitise(r.data?.theme_default_light, FALLBACK_DEFAULT_LIGHT),
        dark: sanitise(r.data?.theme_default_dark, FALLBACK_DEFAULT_DARK),
      };
      setSystemDefaults(next);
      try { localStorage.setItem('app.system_defaults', JSON.stringify(next)); } catch {}
    } catch {
      /* keep cached or fallback values */
    }
  }, []);

  useEffect(() => {
    loadSystemDefaults();
    // Re-pull whenever the SuperAdmin saves new defaults — emitted from
    // AdminBrandingPage.handleSave so all live sessions update.
    const onDefaults = () => loadSystemDefaults();
    window.addEventListener('site-config-updated', onDefaults);
    return () => window.removeEventListener('site-config-updated', onDefaults);
  }, [loadSystemDefaults]);

  useEffect(() => { if (user) loadTheme(); }, [user, loadTheme]);
  useEffect(() => { loadTheme(); }, [loadTheme]);

  const setThemeColor = async (color) => {
    setUserPickedColor(color);
    try { localStorage.setItem('app.theme_color', color); } catch {}
    const token = localStorage.getItem('token');
    if (token) {
      const { success } = await useDocPrefsStore.getState().update({ theme_color: color });
      if (!success) console.error('Failed to save theme color');
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        primaryColor: effective,
        userPickedColor,
        setPrimaryColor: setThemeColor,
        loaded,
        systemDefaults,
        isPro,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
